import { useState } from 'react';
import { createFileRoute, Navigate, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { ArrowLeft, MapPin, Save, X } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { useAuth } from '@/hooks/useAuth';
import { useActiveLocation } from '@/contexts/ActiveLocationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NewOrderStepper } from '@/components/orders/new-order/NewOrderStepper';
import { Step1SelectItems } from '@/components/orders/new-order/Step1SelectItems';
import { Step2CustomerInfo } from '@/components/orders/new-order/Step2CustomerInfo';
import { Step3AssignSubmit } from '@/components/orders/new-order/Step3AssignSubmit';
import { useOrderState } from '@/components/orders/new-order/useOrderState';
import { useIsTablet } from '@/hooks/use-mobile';
import { PageHeader } from '@/components/layout/PageHeaderSlot';
import { LocationSelector } from '@/components/layout/LocationSelector';
import { FeatureGate } from '@/components/auth/FeatureGate';

// ─── Route ───────────────────────────────────────────────────────────────────

const searchSchema = z.object({
  locationId: z.coerce.number().optional(),
  menuId: z.coerce.number().optional(),
});

export const Route = createFileRoute('/(protected)/_layout/new-order')({
  validateSearch: (search) => searchSchema.parse(search),
  component: NewOrderRoute,
});

// Writing a ticket needs somewhere for it to land (T-1106) — without the orders
// board there is nothing to submit to.
function NewOrderRoute() {
  return (
    <FeatureGate feature="kdsOrders" from="/new-order">
      <NewOrderPage />
    </FeatureGate>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function NewOrderPage() {
  const { t } = useLingui();
  const { hasPermission, isLoading } = useAuth();
  const { locationId: locationIdParam, menuId } = Route.useSearch();
  const { activeLocation } = useActiveLocation();

  const [step, setStep] = useState(1);
  const state = useOrderState();
  const isTablet = useIsTablet();

  // Permission gate
  if (!isLoading && !hasPermission('order:create')) {
    return (
      <Navigate
        to="/unauthorized"
        search={{ reason: 'permission_denied', from: '/new-order', requiredPermission: 'order:create' }}
        replace
      />
    );
  }

  // URL param takes precedence (deeplink), otherwise use the global active location
  const resolvedLocationId = locationIdParam ?? activeLocation?.id;
  const resolvedLocationName = activeLocation?.name;

  return (
    <div className="flex flex-col gap-0 overflow-y-auto">
      {/* ── Page header ─────────────────────────────────────────────── */}
      {isTablet ? (
        <PageHeader>
          <NewOrderTabletHeader />
        </PageHeader>
      ) : (
      <div className="flex items-center gap-3 px-3 pt-5 pb-4 border-b border-(--color-border)">
        <Link
          to="/working"
          className="w-9 h-9 rounded-lg border border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) flex items-center justify-center text-(--color-text-primary) shrink-0"
          title={t`Back`}
        >
          <ArrowLeft className="w-4.5 h-4.5" />
        </Link>

        <div className="flex-1">
          <div className="flex items-center gap-2.5">
            <h1 className="text-4xl font-semibold text-(--color-text-primary)">
              <Trans>New Order</Trans>
            </h1>
            <Badge className="bg-(--color-surface-chip-inactive) text-(--color-text-primary) hover:bg-(--color-surface-chip-inactive) border-none rounded-full px-3 py-0.5 text-sm font-medium">
              <Trans>Draft</Trans>
            </Badge>
          </div>
          {resolvedLocationName && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-(--color-text-secondary)">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              {resolvedLocationName}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" className="h-10 px-4 gap-2">
            <Save className="w-4 h-4" />
            <Trans>Save draft</Trans>
          </Button>
          <Button variant="destructive-subtle" size="sm" className="h-10 px-4 gap-2">
            <X className="w-4 h-4" />
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </div>
      )}

      {/* ── Content ─────────────────────────────────────────────────── */}
      <div className="px-3 pt-5 pb-6">
        {!resolvedLocationId ? (
          <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
            <div className="w-12 h-12 rounded-full bg-(--color-surface-muted) flex items-center justify-center">
              <MapPin className="w-6 h-6 text-(--color-text-secondary)" />
            </div>
            <div>
              <p className="text-lg font-semibold text-(--color-text-primary)">
                <Trans>No location selected</Trans>
              </p>
              <p className="mt-1 text-sm text-(--color-text-secondary)">
                <Trans>Select a location from the top bar before creating an order.</Trans>
              </p>
            </div>
          </div>
        ) : (
          <>
            <NewOrderStepper currentStep={step} onGoTo={setStep} />
            <div className="mt-8">
              {step === 1 && (
                <Step1SelectItems
                  state={state}
                  menuId={menuId}
                  onContinue={() => setStep(2)}
                />
              )}
              {step === 2 && (
                <Step2CustomerInfo
                  state={state}
                  locationId={resolvedLocationId}
                  onBack={() => setStep(1)}
                  onContinue={() => setStep(3)}
                />
              )}
              {step === 3 && (
                <Step3AssignSubmit
                  state={state}
                  locationId={resolvedLocationId}
                  menuId={menuId}
                  onBack={() => setStep(2)}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Tablet header ──────────────────────────────────────────────────────────
// On tablet the page owns the app's top bar (rendered into the PageHeader slot),
// so the header is a compact single row: back · title + Draft + location · actions.

function NewOrderTabletHeader() {
  const { t } = useLingui();
  return (
    <div className="flex h-16 items-center gap-3 border-b border-(--color-border) px-5">
      <Link
        to="/working"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-muted)"
        title={t`Back`}
      >
        <ArrowLeft className="h-5 w-5" />
      </Link>

      <div className="flex items-center gap-2 shrink-0">
        <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary)">
          <Trans>New Order</Trans>
        </h1>
        <Badge
          variant="success"
          className="rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold tracking-wide uppercase"
        >
          <Trans>Draft</Trans>
        </Badge>
      </div>

      <LocationSelector triggerClassName="h-9 px-3 rounded-lg bg-(--color-surface-muted) border border-(--color-border) text-[0.8125rem] font-semibold text-(--color-text-primary) shrink-0 transition-colors hover:bg-(--color-surface-accent)" />

      <div className="ml-auto flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-11 px-4 gap-2">
          <Save className="h-4 w-4" />
          <Trans>Save draft</Trans>
        </Button>
        <Button variant="destructive-subtle" size="sm" className="h-11 px-4 gap-2">
          <X className="h-4 w-4" />
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </div>
  );
}
